import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { requestCustomerOtp, verifyCustomerOtp } from "../services/orderPortal";
import { isValidEmail } from "../utils/checkoutOrder";
import { setCustomerSession } from "../utils/customerSession";
import { BtnContent } from "../components/BtnIcon";
import { IoArrowBackOutline, IoMailOutline, IoShieldCheckmarkOutline } from "react-icons/io5";

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectFrom = location.state?.from;
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("email");
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const sendOtp = async () => {
    setError(null);
    setMessage(null);
    if (!isValidEmail(email)) {
      setError("Enter a valid email address.");
      return;
    }
    setLoading(true);
    const res = await requestCustomerOtp(email);
    setLoading(false);
    if (!res.ok) {
      setError(res.message || "Could not send code.");
      return;
    }
    setMessage(res.data?.message || "Check your email for the 6-digit code.");
    setStep("otp");
  };

  const verify = async () => {
    setError(null);
    setMessage(null);
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setLoading(true);
    const res = await verifyCustomerOtp(email, code);
    setLoading(false);
    if (!res.ok || !res.data?.sessionToken) {
      setError(res.message || "Invalid or expired code.");
      return;
    }
    setCustomerSession(res.data.sessionToken, res.data.email || email);
    if (redirectFrom?.fromPath) {
      navigate(redirectFrom.fromPath, {
        replace: true,
        state: {
          openReview: redirectFrom.reviewOpen,
          prefillOrderId: redirectFrom.prefillOrderId || undefined,
          justLoggedIn: true,
        },
      });
    } else {
      navigate("/orders", { replace: true });
    }
  };

  return (
    <div className="container py-5" style={{ maxWidth: "420px" }}>
      <h1 className="h3 mb-4 text-center">Account login</h1>
      <p className="small text-muted text-center mb-4">
        Enter the email you used on a past order. We will send a one-time code only if that email
        appears in our orders. Then you can view history and loyalty rewards.
      </p>

      {step === "email" && (
        <>
          <div className="mb-3">
            <label className="form-label small fw-semibold" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              autoComplete="email"
              placeholder="you@example.com"
            />
          </div>
          <button
            type="button"
            className="btn btn-primary w-100 py-2"
            disabled={loading}
            onClick={sendOtp}
          >
            <BtnContent icon={IoMailOutline}>{loading ? "Sending…" : "Send code"}</BtnContent>
          </button>
        </>
      )}

      {step === "otp" && (
        <>
          <p className="small text-muted mb-2">
            Code sent to <strong>{email}</strong>
          </p>
          <div className="mb-3">
            <label className="form-label small fw-semibold" htmlFor="login-otp">
              6-digit code
            </label>
            <input
              id="login-otp"
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="form-control text-center fs-4 tracking-wide"
              style={{ letterSpacing: "0.35em" }}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
            />
          </div>
          <button
            type="button"
            className="btn btn-primary w-100 py-2 mb-2"
            disabled={loading}
            onClick={verify}
          >
            <BtnContent icon={IoShieldCheckmarkOutline}>
              {loading ? "Verifying…" : "Verify and continue"}
            </BtnContent>
          </button>
          <button
            type="button"
            className="btn btn-link w-100 text-muted small"
            onClick={() => {
              setStep("email");
              setOtp("");
              setMessage(null);
            }}
          >
            <BtnContent icon={IoArrowBackOutline}>Use a different email</BtnContent>
          </button>
        </>
      )}

      {error && <p className="small text-danger mt-3 mb-0">{error}</p>}
      {message && !error && (
        <p className="small text-success mt-3 mb-0">{message}</p>
      )}

      <p className="text-center mt-4 mb-0">
        <Link to="/" className="text-muted small">
          ← Back to home
        </Link>
      </p>
    </div>
  );
}

export default Login;
